import React from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { OptimizedImage } from "@/components/ui/OptimizedImage";
import { ApparelProduct } from "@/types/models";
import { formatCurrency } from "@/lib/format";
import { ShoppingBag, Edit, Trash2, EyeIcon, Check } from "lucide-react";
import { motion } from "framer-motion";

interface ApparelProductCardProps {
  product: ApparelProduct;
  onEdit?: (product: ApparelProduct) => void;
  onDelete?: (product: ApparelProduct) => void;
  onSelect?: (product: ApparelProduct) => void;
  showActions?: boolean;
  isAdmin?: boolean;
}

const ApparelProductCard: React.FC<ApparelProductCardProps> = ({
  product,
  onEdit,
  onDelete,
  onSelect,
  showActions = true,
  isAdmin = false,
}) => {
  const handleSelect = () => {
    if (onSelect) onSelect(product);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) onEdit(product);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) onDelete(product);
  };

  // Check if product is out of stock
  const isOutOfStock = product.has_inventory && 
    product.inventory_count !== null && 
    product.inventory_count <= 0;

  return (
    <Card 
      className={`overflow-hidden border border-border/30 transition-all duration-300 hover:shadow-md ${
        isOutOfStock ? "opacity-70" : ""
      } ${showActions ? "cursor-pointer" : ""}`}
      onClick={showActions ? handleSelect : undefined}
    >
      <div className="relative aspect-square overflow-hidden bg-muted/20">
        {product.image_url ? (
          <div className="w-full h-full relative overflow-hidden group">
            <OptimizedImage
              src={product.image_url}
              alt={product.name}
              width={400}
              height={400}
              className="w-full h-full transition-transform duration-700 group-hover:scale-105"
              objectFit="cover"
              rounded="none"
            />
            {showActions && !isOutOfStock && !isAdmin && (
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                <Button 
                  variant="secondary"
                  size="sm"
                  className="bg-white/80 backdrop-blur-sm shadow-lg hover:bg-white"
                  onClick={handleSelect}
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  View
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full h-full bg-muted/10 flex items-center justify-center">
            <ShoppingBag className="h-12 w-12 text-muted-foreground opacity-20" />
          </div>
        )}
        
        {/* Status badges */}
        <div className="absolute top-3 right-3 flex flex-col gap-1">
          {isAdmin && product.status !== "active" && (
            <Badge variant="outline" className="bg-yellow-50 border-amber-200 text-amber-700 font-medium">
              {product.status}
            </Badge>
          )}
          
          {isOutOfStock && (
            <Badge variant="outline" className="bg-red-50 border-red-200 text-red-700 font-medium">
              Out of Stock
            </Badge>
          )}
        </div>
      </div>
      
      <CardContent className="pt-4">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-medium text-base leading-tight">{product.name}</h3>
            <p className="text-muted-foreground text-sm mt-0.5">
              {product.category}
            </p>
          </div>
          <span className="font-semibold text-base">
            {formatCurrency(product.price_cents / 100)}
          </span>
        </div>
        
        {product.description && (
          <p className="text-sm mt-2 line-clamp-2 text-muted-foreground">
            {product.description}
          </p>
        )}
        
        {product.has_inventory && product.inventory_count !== null && (
          <p className="text-xs mt-2 text-muted-foreground flex items-center gap-1">
            <span className={`w-2 h-2 rounded-full ${product.inventory_count > 5 ? 'bg-green-500' : product.inventory_count > 0 ? 'bg-amber-500' : 'bg-red-500'}`}></span>
            {product.inventory_count} in stock
          </p>
        )}
      </CardContent>
      
      {(showActions && isAdmin) && (
        <CardFooter className="flex justify-end space-x-2 pt-0 pb-3">
          <Button size="sm" variant="ghost" onClick={handleEdit}>
            <Edit className="h-4 w-4 mr-1" /> Edit
          </Button>
          <Button size="sm" variant="destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
        </CardFooter>
      )}
      
      {(showActions && !isAdmin) && (
        <CardFooter className="flex justify-end space-x-2 pt-0 pb-3">
          <Button 
            size="sm" 
            onClick={handleSelect}
            disabled={isOutOfStock}
            className={`${isOutOfStock ? "" : "bg-hivetivity-primary hover:bg-hivetivity-primary/90"} shadow-sm transition-all`}
          >
            {isOutOfStock ? "Out of Stock" : "View Details"}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default ApparelProductCard;
